import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { classes } from "portal/theme/theme";
import { DATE_PATH_FORMAT } from "portal/utils/dates";
import { DateTime } from "luxon";
import {
  GridActionsColDef,
  GridColDef,
  GridToolbarExport,
} from "@mui/x-data-grid-premium";
import { portalApi } from "portal/state/portalApi";
import { SearchField } from "portal/components/header/SearchField";
import { skipToken } from "@reduxjs/toolkit/query";
import { titleCase } from "common/utils/strings";
import { useJigsawMetrics } from "../../utils/hooks/useJigsawMetrics";
import { useParams } from "react-router-dom";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { valueOrLoading } from "portal/utils/strings";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import React, { FunctionComponent, useMemo, useState } from "react";

const defaultColumn: Partial<GridColDef> = {
  sortable: true,
  disableColumnMenu: true,
};

interface ModuleHistoryRow {
  rowKey: string;
  rowId: number;
  moduleId: number;
  moduleSerial: string;
  disabled: boolean;
  startDate: number;
  endDate: number | undefined;
}

const _RobotWeedingModulesHistory: FunctionComponent = () => {
  const { serial } = useParams();
  const { i18n, t } = useTranslation();
  const useJigsaw = useJigsawMetrics();

  const {
    data: history,
    isLoading,
    isError,
  } = useQueryPopups(
    portalApi.endpoints.getReaperConfigurationHistory.useQuery(
      serial ? { serial } : skipToken
    )
  );

  const { data: summary } = useQueryPopups(
    portalApi.endpoints.getRobot.useQuery(
      serial ? { language: i18n.language, serial, useJigsaw } : skipToken
    )
  );

  const allRows = useMemo<ModuleHistoryRow[]>(() => {
    if (!history?.periods) {
      return [];
    }
    return history.periods.map((p) => ({
      rowKey: `${p.rowId}_${p.moduleId}_${p.startDate?.timestampMs ?? 0}`,
      rowId: p.rowId,
      moduleId: p.moduleId,
      moduleSerial: p.serial,
      disabled: p.disabled,
      startDate: p.startDate?.timestampMs ?? 0,
      endDate: p.endDate?.timestampMs ?? undefined,
    }));
  }, [history]);

  const [rowFilter, setRowFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [serialFilter, setSerialFilter] = useState("");

  const results = useMemo(() => {
    return allRows.filter(
      (row) =>
        String(row.rowId).includes(rowFilter) &&
        String(row.moduleId).includes(moduleFilter) &&
        row.moduleSerial.toLowerCase().includes(serialFilter.toLowerCase())
    );
  }, [allRows, rowFilter, moduleFilter, serialFilter]);

  const columns: Array<GridColDef<ModuleHistoryRow> | GridActionsColDef> = [
    {
      ...defaultColumn,
      field: "startDate",
      headerName: titleCase(t("utils.descriptors.started")),
      width: 175,
      type: "dateTime",
      valueGetter: (value) => new Date(value as number),
    },
    {
      ...defaultColumn,
      field: "endDate",
      headerName: titleCase(t("utils.descriptors.ended")),
      width: 175,
      type: "dateTime",
      valueGetter: (value: any) =>
        value === undefined ? undefined : new Date(value as number),
      sortComparator: (a: Date | undefined, b: Date | undefined) => {
        const aTime = a ? a.getTime() : Infinity;
        const bTime = b ? b.getTime() : Infinity;
        return aTime - bTime;
      },
      renderCell: ({ value }) =>
        value ? (
          <span className="font-mono">{(value as Date).toLocaleString()}</span>
        ) : (
          <span className="text-green-500">
            {t("utils.descriptors.active")}
          </span>
        ),
    },
    {
      ...defaultColumn,
      field: "rowId",
      headerName: titleCase(t("models.modules.fields.rowNumber")),
      cellClassName: "font-mono",
      sortComparator: new Intl.Collator(undefined, { numeric: true }).compare,
      width: 75,
    },
    {
      ...defaultColumn,
      field: "moduleId",
      headerName: titleCase(t("models.modules.module_one")),
      cellClassName: "font-mono",
      sortComparator: new Intl.Collator(undefined, { numeric: true }).compare,
      width: 100,
    },
    {
      ...defaultColumn,
      field: "moduleSerial",
      headerName: t("models.modules.fields.serial.value"),
      cellClassName: ({ value }) =>
        classes({ "font-mono": value, "text-gray-500": !value }),
      width: 175,
      valueFormatter: (value) =>
        (value as string) || t("models.modules.fields.serial.unassigned"),
    },
    {
      ...defaultColumn,
      field: "disabled",
      headerName: t("models.modules.fields.isEnabled"),
      cellClassName: "font-mono",
      width: 125,
      valueFormatter: (value) =>
        (value as boolean)
          ? t("utils.descriptors.disabled")
          : t("utils.descriptors.enabled"),
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {isError ? (
        <span>{t("components.ModuleTable.modulesUnavailable")}</span>
      ) : (
        <CarbonDataGrid<ModuleHistoryRow>
          getRowId={(row) => row.rowKey}
          header={
            <>
              <SearchField
                value={rowFilter}
                onChange={setRowFilter}
                label={t("utils.actions.searchLong", {
                  subject: titleCase(t("models.modules.fields.rowNumber")),
                })}
              />
              <SearchField
                value={moduleFilter}
                onChange={setModuleFilter}
                label={t("utils.actions.searchLong", {
                  subject: titleCase(t("models.modules.module_one")),
                })}
              />
              <SearchField
                value={serialFilter}
                onChange={setSerialFilter}
                label={t("utils.actions.searchLong", {
                  subject: t("models.modules.fields.serial.value"),
                })}
              />
              <div className="flex items-center">
                <GridToolbarExport
                  csvOptions={{
                    fileName: t("components.ModuleTable.export", {
                      robots: valueOrLoading(summary?.robot?.customerSerial, t),
                      date: DateTime.local().toFormat(DATE_PATH_FORMAT),
                    }),
                  }}
                  excelOptions={{ disableToolbarButton: true }}
                  printOptions={{ disableToolbarButton: true }}
                />
              </div>
            </>
          }
          className={classes("flex flex-1")}
          loading={isLoading || !history}
          rows={results}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [
                { field: "endDate", sort: "desc" },
                { field: "startDate", sort: "desc" },
                { field: "rowId", sort: "asc" },
                { field: "moduleId", sort: "asc" },
              ],
            },
          }}
          hideFooter
          disableRowSelectionOnClick
        />
      )}
    </div>
  );
};

export const RobotWeedingModulesHistory = withAuthenticationRequired(
  _RobotWeedingModulesHistory
);
